import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import IdleTracker from 'idle-tracker';

import config from 'Config';
import { I13N_TYPE_CLEAN_UP } from 'Lib/constants/i13nSpec';

import { beginLongIdle, beginShortIdle, endLongIdle, endShortIdle } from 'Store/commonActions';
import { updateNotificationsType } from 'Store/notifications/actions';
import { NotificationType } from 'Store/notifications/types';
import { fetchProfile } from 'Store/profile/actions';

const { timeout: SHORT_IDLE_TRACKER_TIMEOUT, enabled: SHORT_IDLE_TRACKER_ENABLED } =
  config.shortIdleTracker;
const { timeout: LONG_IDLE_TRACKER_TIMEOUT, enabled: LONG_IDLE_TRACKER_ENABLED } =
  config.longIdleTracker;

const useIdleTracker = ({
  loggedIn,
  executeEvent,
}: {
  loggedIn: boolean;
  executeEvent: (eventName: string, values?: Record<string, any>) => void;
}) => {
  const shortIdleTracker = useRef<IdleTracker | null>(null);
  const longIdleTracker = useRef<IdleTracker | null>(null);
  const dispatch = useDispatch();

  const onShortIdleCallback = useCallback(
    (e) => {
      const isIdle = e.idle;
      dispatch(updateNotificationsType(isIdle ? NotificationType.browser : NotificationType.inApp));
      if (isIdle) {
        dispatch(beginShortIdle());
      } else {
        if (loggedIn) {
          // status changed from idle state
          dispatch(fetchProfile());
        }
        dispatch(endShortIdle());
      }
    },
    [loggedIn, dispatch]
  );

  const onLongIdleCallback = useCallback(
    (e) => {
      const isIdle = e.idle;
      if (isIdle) {
        dispatch(beginLongIdle());
        executeEvent(I13N_TYPE_CLEAN_UP);
      } else {
        dispatch(endLongIdle());
      }
    },
    [executeEvent, dispatch]
  );

  useEffect(() => {
    let shortIdleTrackerInstance, longIdleTrackerInstance;
    if (loggedIn && SHORT_IDLE_TRACKER_ENABLED) {
      if (!shortIdleTracker.current) {
        shortIdleTrackerInstance = new IdleTracker({ timeout: SHORT_IDLE_TRACKER_TIMEOUT });
        shortIdleTracker.current = shortIdleTrackerInstance;
      }
      shortIdleTrackerInstance?.start({
        onIdleCallback: onShortIdleCallback,
      });
    }

    if (loggedIn && LONG_IDLE_TRACKER_ENABLED) {
      if (!longIdleTracker.current) {
        longIdleTrackerInstance = new IdleTracker({ timeout: LONG_IDLE_TRACKER_TIMEOUT });
        longIdleTracker.current = longIdleTrackerInstance;
      }
      longIdleTrackerInstance?.start({
        onIdleCallback: onLongIdleCallback,
      });
    }

    return () => {
      if (shortIdleTrackerInstance) {
        shortIdleTrackerInstance.end();
        shortIdleTracker.current = null;
      }
      if (longIdleTrackerInstance) {
        longIdleTrackerInstance.end();
        longIdleTrackerInstance.current = null;
      }
    };
  }, [loggedIn, onShortIdleCallback, onLongIdleCallback]);
};

export default useIdleTracker;
